import { ChevronDown } from 'lucide-react';

import { Section } from '@/components/ui/Section';
import { beatIdOf } from '@/lib/routing';
import type { Slide } from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

interface Props {
  slide: Slide;
}

type RoutingKey = 'default' | 'ifExpanded' | 'ifReteach' | 'ifSkipped';

const ROUTING_LABELS: Record<RoutingKey, { label: string; hint: string }> = {
  default: {
    label: 'Default next',
    hint: 'Where the lesson goes after this slide finishes.',
  },
  ifExpanded: {
    label: 'After expand',
    hint: "Used when the student triggered an 'expand previous' detour and now returns.",
  },
  ifReteach: {
    label: 'After reteach',
    hint: 'Used after a quiz reteach completes.',
  },
  ifSkipped: {
    label: 'If skipped',
    hint: 'Used when this slide was skipped.',
  },
};

/** Special select value representing the lesson-terminator (`null`). */
const END_OF_LESSON = '__END__';

/**
 * Routing dropdowns for `beatMeta.next.{default,ifExpanded,ifReteach,ifSkipped}`.
 * Each dropdown lists only beat ids that actually exist in the current pack
 * (prevents the editor from minting dangling pointers via the UI), plus an
 * "End lesson" option that writes `null`.
 */
export function RoutingEditor({ slide }: Props) {
  const actions = useEditorActions();
  const allSlides = useEditorStore((s) => s.script?.slides ?? []);
  const next = slide.beatMeta?.next ?? {};

  const options = buildBeatOptions(allSlides, slide.id);

  return (
    <Section title="Routing">
      <p className="-mt-1 mb-2 text-[11px] text-fg-subtle">
        The lesson follows these pointers to decide what comes next — not the
        order slides appear in the editor.
      </p>

      {(Object.keys(ROUTING_LABELS) as RoutingKey[]).map((key) => {
        const value = next[key];
        return (
          <div key={key} className="mb-2.5 last:mb-0">
            <label className="mb-1 flex items-center justify-between text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fg-muted">
              <span>{ROUTING_LABELS[key].label}</span>
            </label>
            <BeatSelect
              value={value ?? END_OF_LESSON}
              options={options}
              onChange={(v) =>
                actions.updateRouting(slide.id, {
                  [key]: v === END_OF_LESSON ? null : v,
                })
              }
            />
            <p className="mt-0.5 text-[10.5px] text-fg-subtle">
              {ROUTING_LABELS[key].hint}
            </p>
          </div>
        );
      })}
    </Section>
  );
}

function BeatSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-8 w-full appearance-none rounded-md border border-border bg-white pl-2 pr-7 text-[12px] text-fg',
          'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20',
        )}
      >
        <option value={END_OF_LESSON}>End lesson (null)</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={12}
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-fg-subtle"
      />
    </div>
  );
}

/** Enumerate every beat id in the pack as a dropdown option. Excludes
 *  `selfSlideId` so the editor doesn't let teachers create self-loops via
 *  the UI. */
function buildBeatOptions(
  slides: Slide[],
  selfSlideId: string,
): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  for (const s of slides) {
    if (s.id === selfSlideId) continue;
    const beatId = beatIdOf(s);
    if (!beatId) continue;
    out.push({
      value: beatId,
      label: `${s.label || 'Untitled'} · ${beatId}`,
    });
  }
  return out;
}
